// Library for Events that are strongly typed
// see https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';

import { PageMobileConfigType, SearchResult } from './PageTypes';

import { DataServices } from '../utils/DataServices';
import { PageParser } from './PageParser';
import { PageEngine } from './PageEngine';

import { AuditLog } from '../Participant/AuditLog';
import { AuditLogType } from '../Participant/AuditLogTypes';
import { LogicEngine } from '../LogicEngine/LogicEngine';

class PageManagerConfig
{
	_Config: PageMobileConfigType | undefined;
	_PageParser: PageParser | undefined;

	constructor() { }

	/** Returns a reference to the MobileConfig File */
	async getMobileConfig(filename: string): Promise<PageMobileConfigType>
	{
		if (!this._Config)
		{
			const dataService = DataServices.instance();
			this._Config = await dataService.getMobileConfig(filename);
		}

		return this._Config;
	}

	async getParser(filename: string): Promise<PageParser>
	{
		if (!this._PageParser)
		{
			let config = await this.getMobileConfig(filename);
			this._PageParser = new PageParser(config);
		}

		return this._PageParser;
	}
}

/**
 * Wrapper class that manages to load the EDC Configuration and
 * Mobile configuration files as needed in order to load the pages.
 *
 * Optional events evOnLoad and evOnReady allow you to display a loading
 * page or spinner during network actviity.
 *
 * @example
 * PageManager.allowEdcConfig = true;
 * let page = await PageManager.instance().findRoute("/page/enroll")
 */
export class PageManager
{
	public static allowMobileConfig: boolean = true;
	public static allowEdcConfig: boolean = false;
	public static allowSystemPages: boolean = true;
	public static allowNotesPages: boolean = true;

	public static allowSchedulePage: boolean = true;
	public static allowAe: boolean = true;
	public static allowAdmin: boolean = true;
	public static allowLabData: boolean = true;
	public static allowReports: boolean = true;

	// global variable shared between pages
	public static globalPageValues: { [key: string]: any } = {};

	/** Set to true when you are loading this page as part of an include so that it doesn't audit */
	loadingAsLibrary: boolean = false;

	// Reference to the header page that is included in all pages
	// before those pages run.
	_header: PageEngine | undefined;

	/**
	 * Event that Fired when we start loading the engine the
	 * first time.  This can be used to display a spinner or
	 * loading status while the network or cache is loading the
	 * configuration files.
	 *
	 * @example
	 * PageManager.instance().evOnLoad.on( (stringMessage) => { } );
	 */
	evOnLoad: SubEvent<string> = new SubEvent();

	/**
	 * Event that is fired when the PageManager is ready and the
	 * configuration files are available.
	 *
	 * @example
	 * PageManager.instance().evOnReady.on( (stringMessage) => { } );
	 */
	evOnReady: SubEvent<string> = new SubEvent();

	// References to cache the configuration files
	_configManagers: { [key: string]: PageManagerConfig } = {};

	/**
	 * PageManager is a single instance class pattern and should
	 * be loaded through the instance function.
	 * @example
	 * PageManager.instance()
	 */
	protected static _instance: PageManager | undefined;

	/** Return an instance of the PageManager */
	static instance(): PageManager
	{
		if (!PageManager._instance)
		{
			PageManager._instance = new PageManager();
		}

		return PageManager._instance;
	}

	async findHeader()
	{
		if (this._header) return;
		this._header = await this.findRoute('header');
	}

	/**
	 * Use the audit log to record the page that was requested for logging
	 * @param routePath
	 */
	async logPageLoad(routePath: string)
	{
		if (this.loadingAsLibrary) return;

		const auditText = `PageLoad ${routePath}`;

		// Brian - Don't log the header load event.
		if (routePath != 'header')
		{
			// Setup debugging variables
			// TODO:  This method doesn't account for "header" or redirect or include files.
			LogicEngine.currentPageShowing = routePath;
			LogicEngine.currentLineNumber = 0;

			// Submit a page load audit record
			const audit = new AuditLog('', auditText);
			audit.log.event_type = AuditLog.EventTypes.PageLoad;
			audit.setLinkedRoute(routePath);
			await audit.save(); // Background save
		}
	}

	/**
	 * Load a named config file and search for the route path.
	 * Returns a PageEngine or undefined if the route was not matched.
	 *
	 * @param strFilename
	 * @param routePath
	 * @returns
	 */
	async processConfigFile(
		strFilename: string,
		routePath: string,
	): Promise<PageEngine | undefined>
	{
		// Load the given config file and check to see if a matching
		// route is contained in the file.
		if (!this._configManagers[strFilename])
			this._configManagers[strFilename] = new PageManagerConfig();

		const parser = await this._configManagers[strFilename].getParser(strFilename);
		const engine = await parser.findRoute(routePath);

		// Engine is valid only when a matching page was found
		if (engine.isValid())
		{
			// If routePath is already "header" it is because we are getting
			// called from findHeader() function so we don't want to call it again.
			if (routePath != 'header')
			{
				await this.findHeader();
				if (this._header)
				{
					engine.importPage(this._header);
				}
			}

			// import globalPageVariables to logicEngine
			engine.logic.import(PageManager.globalPageValues);

			// Look for library pages
			let libraryList = await parser.findLibraries();
			for (const libPage of libraryList) engine.importPage(libPage);

			this.evOnReady.emit('Loaded');
			return engine;
		}

		return undefined;
	}

	/**
	 * Search through the routes in MobileConfig and EDC Portal.
	 * If allowEdcPortal is true then we will search there first.
	 *
	 * @param routePath
	 */
	async findRoute(routePath: string): Promise<PageEngine>
	{
		// Allow the mobile device to start a spinner or loading screen.
		this.evOnLoad.emit('Loading');

		// Remove spaces
		routePath = routePath.replace(/ /g, '');

		// Remove the nocache code which is "XX" and 8 letters.
		let reNoCache = new RegExp('XX[A-Z]{6,9}.', 'g');
		routePath = routePath.replace(reNoCache, '');

		// Remove trailing parameters
		let paramIndex = routePath.indexOf('?');
		if (paramIndex != -1)
		{
			const queryString = routePath.substring(paramIndex + 1);
			routePath = routePath.substring(0, paramIndex);
			// console.log("Removed query string:", queryString);
		}

		// Don't await this, let it run in the background
		this.logPageLoad(routePath);

		if (PageManager.allowEdcConfig)
		{
			const engine = await this.processConfigFile('EDC Portal.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowMobileConfig)
		{
			const engine = await this.processConfigFile('MobileConfig.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowSystemPages)
		{
			const engine = await this.processConfigFile('TaskCheck.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowSystemPages)
		{
			const engine = await this.processConfigFile('SystemPages.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowSystemPages)
		{
			const engine = await this.processConfigFile('TestingHappy.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowSystemPages)
		{
			const engine = await this.processConfigFile('TestingUnhappy.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowNotesPages)
		{
			const engine = await this.processConfigFile('Notes.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowSchedulePage)
		{
			const engine = await this.processConfigFile('SchedulingPages.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowAe)
		{
			const engine = await this.processConfigFile('AdverseEventsPages.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowAdmin)
		{
			const engine = await this.processConfigFile('AdministrationPages.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowLabData)
		{
			const engine = await this.processConfigFile('LabDataPages.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowReports)
		{
			const engine = await this.processConfigFile('ReportingPages.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		if (PageManager.allowReports)
		{
			const engine = await this.processConfigFile('DashboardPages.json', routePath);
			if (engine && engine.isValid()) return engine;
		}

		console.warn('PageManager.findRoute unable to find route for ', routePath);
		this.evOnReady.emit('Unable to find route.');

		return new PageEngine({
			tableName: '',
			meta: {},
			data: [],
		});
	}

	/**
	 * Search PageContents
	 *
	 * @param pattern
	 * @returns
	 */
	async searchPageContents(pattern: string | RegExp): Promise<SearchResult[]>
	{
		// Allow the mobile device to start a spinner or loading screen.
		this.evOnLoad.emit('Loading');

		const searchResults: SearchResult[] = [];
		if (PageManager.allowEdcConfig)
		{
			const result = await this.searchConfigFile('EDC Portal.json', pattern);
			searchResults.push(result);
		}

		if (PageManager.allowMobileConfig)
		{
			const result = await this.searchConfigFile('MobileConfig.json', pattern);
			searchResults.push(result);
		}

		if (PageManager.allowSystemPages)
		{
			const result = await this.searchConfigFile('SystemPages.json', pattern);
			searchResults.push(result);
		}

		if (PageManager.allowNotesPages)
		{
			const result = await this.searchConfigFile('Notes.json', pattern);
			searchResults.push(result);
		}

		if (PageManager.allowSchedulePage)
		{
			const result = await this.searchConfigFile('SchedulingPages.json', pattern);
			searchResults.push(result);
		}

		if (PageManager.allowAe)
		{
			const result = await this.searchConfigFile('AdverseEventsPages.json', pattern);
			searchResults.push(result);
		}

		if (PageManager.allowAdmin)
		{
			const result = await this.searchConfigFile('AdministrationPages.json', pattern);
			searchResults.push(result);
		}

		if (PageManager.allowLabData)
		{
			const result = await this.searchConfigFile('LabDataPages.json', pattern);
			searchResults.push(result);
		}

		if (PageManager.allowReports)
		{
			const result = await this.searchConfigFile('ReportingPages.json', pattern);
			searchResults.push(result);
		}

		return searchResults;
	}

	/**
	 * Load a named config file and search for commands.
	 *
	 * @param strFilename
	 * @param routePath
	 * @returns
	 */
	async searchConfigFile(
		strFilename: string,
		searchPattern: string | RegExp,
	): Promise<SearchResult>
	{
		if (!this._configManagers[strFilename])
			this._configManagers[strFilename] = new PageManagerConfig();

		const parser = await this._configManagers[strFilename].getParser(strFilename);
		const result = parser.search(searchPattern);
		return {
			fileName: strFilename,
			sheets: result,
		};
	}

	/**
	 * Import some value or function.  See mathjs import function.
	 *
	 * @param something An object to import or a compatible function
	 *
	 * @example
	 * import({ age : 50 });
	 *
	 */
	static import(something: { [key: string]: any })
	{
		Object.assign(this.globalPageValues, something);
	}

	/**
	 *  Assign a variable name to a value.  Similar to import but for a single item
	 *
	 * @param variableName  A key name for value
	 * @param value         A value to assign
	 *
	 * @example
	 * assign("example", { key: "example" })
	 */
	static assign(variableName: string, value: any)
	{
		this.globalPageValues[variableName] = value;
	}

	/**
	 * reset globalPageVariables
	 */
	static resetPageValues()
	{
		this.globalPageValues = {};
	}
	constructor() { }
}
